import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Game from '../components/game.js';
import Q2Toolbar from '../components/Q2Toolbar.js';
import { q2StateOf } from '../components/Q2Badge.js';
import { requestFixtures, requestDay, getStatus } from '../services/q2Api.js';
import { parseSlateDate, shiftSlateDate, todaySlateDate } from '../utils/slateDate.js';
import Container from 'react-bootstrap/Container';
import Col from 'react-bootstrap/Col';
import { useAppState } from '../AppStateContext';
import SuggestionsBox from '../components/suggestionsbox.js';
import ErrorBoundary from '../components/ErrorBoundary.js';
import { Oval } from 'react-loading-icons';


import Card from 'react-bootstrap/Card';
import { styles } from '../styles/styles';
import { hapticTap } from '../utils/haptics';

const formatTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const CardButton = ({ title, onClick }) => (
  <Card
    onClick={() => { hapticTap(); if (onClick) onClick(); }}
    className="game-card"
    style={styles.cardbutton_third}
  >
    <Card.Body>
      <Card.Title style={{ color: 'white', textAlign: 'center' }}>{title}</Card.Title>
    </Card.Body>
  </Card>
);

const FlatCardButton = ({ title, onClick }) => (
  <Card
    onClick={() => { hapticTap(); if (onClick) onClick(); }}
    className="game-card"
    style={styles.cardbutton_flat}
  >
    <Card.Body style={{ padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Card.Title style={{ color: 'white', textAlign: 'center', margin: 0 }}>{title}</Card.Title>
    </Card.Body>
  </Card>
);

// Previous / Refresh+Today / Next, rendered identically under a populated slate and
// under an empty one. They used to be two separate copies of a Bootstrap Row,
// and the copies had drifted: the empty-state one declared columns `6 / 4 / 6`
// — sixteen of twelve — so it wrapped, while both sized each button at 49vw
// inside a 33% column so it overflowed. One component, one grid, three equal
// tracks; there is no longer an arithmetic to get wrong.
//
// The middle track holds two flatter buttons stacked, Refresh over Today, so the
// strip stays three equal tracks.
const DayNav = ({ onPrev, onRefresh, onToday, onNext }) => (
  <div style={styles.daynav}>
    <CardButton title="Previous Day" onClick={onPrev} />
    <div style={styles.daynav_stack}>
      <FlatCardButton title="Refresh" onClick={onRefresh} />
      <FlatCardButton title="Today" onClick={onToday} />
    </div>
    <CardButton title="Next Day" onClick={onNext} />
  </div>
);


const ListOfGames = ({ league }) => {
  // The day is the URL's `?date=YYYY-MM-DD`, not component or App state, so a
  // reload keeps it and a slate can be linked to. Absent or malformed means today;
  // `parseSlateDate` builds a LOCAL date, never the UTC-midnight `new Date(str)`.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedDate = searchParams.get('date');
  const selectedDate = parseSlateDate(requestedDate);   // null => today
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [groupedData, setGroupedData] = useState({});
  const { isAuthenticated, getWithExpiry, setWithExpiry, userProfile, userTier } = useAppState();

  // ---- Operator-only: on-demand AI summarization -------------------------
  // Every one of these is inert for a non-staff visitor. `isStaff` gates the
  // render, the polling and the requests, so the public slate is unchanged.
  const isStaff = Boolean(isAuthenticated && userProfile?.is_staff);
  const [q2States, setQ2States] = useState({});     // fixture id -> state
  const [q2Counts, setQ2Counts] = useState({});
  const [q2Requested, setQ2Requested] = useState({});
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [q2Busy, setQ2Busy] = useState(false);
  const [q2Message, setQ2Message] = useState('');
  // The league's Q2 policy, from the status poll. Drives the badge and toolbar copy.
  const [q2Policy, setQ2Policy] = useState('auto');
  const pollRef = useRef(null);
  // Mirrors `q2Requested` for the polling loop to read. A ref rather than the
  // state itself, so that marking a fixture optimistically does not tear down
  // and rebuild the poll effect mid-cycle.
  const q2RequestedRef = useRef({});
  useEffect(() => { q2RequestedRef.current = q2Requested; }, [q2Requested]);

  const dateStr = selectedDate ? requestedDate : todaySlateDate();
  const isPastDay = dateStr < todaySlateDate();

  const pollStatus = useCallback(async () => {
    if (!isStaff) return {};
    try {
      const { data } = await getStatus(league, dateStr);
      // Every fixture the endpoint returned goes in, null states included --
      // the presence check above depends on it.
      const map = {};
      (data.fixtures || []).forEach((f) => { map[f.id] = f.q2_state; });
      setQ2States(map);
      setQ2Counts(data.counts || {});
      if (data.policy) setQ2Policy(data.policy);
      // Drop the optimistic flag once the poll has SEEN the fixture -- keyed on
      // presence, not truthiness.
      //
      // `if (map[id])` was wrong in the one case that matters: a fixture whose
      // terminal state is null. Null is falsy, so the flag was never cleared and
      // the badge rendered "Queued" forever, for work that had already finished.
      // Measured live on Tigers v Dodgers 2026-08-30 -- the daemon accepted and
      // resolved the request in two seconds and the pill stayed gold until reload.
      setQ2Requested((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((id) => {
          if (Object.prototype.hasOwnProperty.call(map, id)) delete next[id];
        });
        return next;
      });
      return data.counts || {};
    } catch (e) {
      // A 403 here just means "not staff after all" — nothing to report.
      return {};
    }
  }, [isStaff, league, dateStr]);

  // `replace` so stepping through days does not fill history: Back still means
  // "leave the slate", and the day survives a reload either way.
  const handleDateChange = (direction) => {
    setSearchParams({ date: shiftSlateDate(dateStr, direction === 'next' ? 1 : -1) }, { replace: true });
  };

  const handleToday = () => setSearchParams({}, { replace: true });

  const fetchData = useCallback(async (bypassCache = false) => {
    setLoading(true);
  
    const profileID = userProfile?.id || null;
    
    const currentDate = dateStr;
    
    const cacheKey = `fixtures_${league}_${profileID}_${currentDate}`;
    
    if (!bypassCache) {
      const cachedData = getWithExpiry(cacheKey);
      if (cachedData) {
        setGroupedData(cachedData);
        setLoading(false);
        return;
      }
    }
  
    try {
      const url = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Token ${token}` } : {};
      
      setLoadError(false);
      const { data: response } = await axios.get(`${url}/getFixtures/`, {
        params: { league: league, date: currentDate },
        headers: headers
      });
  
      // Group fixtures by exact game time
      const grouped = response.reduce((acc, fixture) => {
        const gameTime = formatTime(fixture.game_time);
        if (!acc[gameTime]) {
          acc[gameTime] = [];
        }
        acc[gameTime].push(fixture);
        return acc;
      }, {});
  
      Object.keys(grouped).forEach(key => {
        grouped[key].sort((a, b) => {
          const spreadA = Math.abs(parseFloat(a.home_spread));
          const spreadB = Math.abs(parseFloat(b.home_spread));
          return spreadA - spreadB;
        });
      });
  
      const sortedGroupedData = Object.keys(grouped)
        .sort((a, b) => new Date('1970/01/01 ' + a) - new Date('1970/01/01 ' + b))
        .reduce((acc, key) => {
          acc[key] = grouped[key];
          return acc;
        }, {});
  
      setGroupedData(sortedGroupedData);
      setWithExpiry(cacheKey, sortedGroupedData);
    } catch (error) {
      // A failed fetch is not an empty slate. Without this the catch fell
      // through to setLoading(false) with groupedData still {}, and the user was
      // told "No games scheduled for <date>" — a factual claim about the sports
      // calendar, made on the strength of a backend outage.
      console.error(error.message);
      setLoadError(true);
    }
    setLoading(false);
  }, [league, dateStr, getWithExpiry, setWithExpiry, userProfile, setGroupedData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset per-day operator state when the slate changes underneath it.
  useEffect(() => {
    setSelectedIds([]);
    setQ2Requested({});
    setQ2States({});
    setQ2Counts({});
    setQ2Message('');
  }, [league, dateStr]);

  // Poll only while something is actually in flight, and stop when it settles.
  // A slate with nothing queued should cost zero requests -- this runs on the
  // public league page, and a permanent 5-second timer there would be a
  // background load on the API for no information.
  useEffect(() => {
    if (!isStaff) return undefined;
    let cancelled = false;

    const tick = async () => {
      const counts = await pollStatus();
      if (cancelled) return;
      const inFlight = (counts.queued || 0) + (counts.processing || 0);
      const optimistic = Object.keys(q2RequestedRef.current).length;
      pollRef.current = setTimeout(tick, inFlight || optimistic ? 5000 : 60000);
    };
    tick();

    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [isStaff, pollStatus]);

  const toggleSelect = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const markRequested = (ids) => {
    setQ2Requested((prev) => {
      const next = { ...prev };
      ids.forEach((id) => { next[id] = true; });
      return next;
    });
  };

  const runRequest = async (fn, ids) => {
    setQ2Busy(true);
    setQ2Message('');
    try {
      const { data } = await fn();
      // Optimistic, and necessary: the POST only reaches a queue. Nothing has
      // claimed the message yet, so no state has replicated back, and without
      // this the button appears to do nothing for a poll cycle or two.
      if (ids) markRequested(ids);
      setQ2Message(`Queued ${data.requested}`);
      setSelectedIds([]);
      setSelectionMode(false);
      setTimeout(pollStatus, 1500);
    } catch (e) {
      const detail = e.response?.data?.error || e.message;
      setQ2Message(`Failed: ${detail}`);
    }
    setQ2Busy(false);
  };

  const allFixtures = Object.values(groupedData).flat();

  return (
    <Container>
      <Col>
        {loading ? (
          <div className="d-flex justify-content-center" style={{ marginTop: '20px' }}>
            <Oval strokeWidth={5} height={100} width={100} />
          </div>
        ) : loadError ? (
          <div className="text-center">
            <p style={{ color: 'white', marginTop: '20px', fontSize: 'clamp(10px, calc(8px + (4) * ((100vw - 300px) / (1200 - 320))), 18px)' }}>
              Couldn't load games. Check your connection and try again.
            </p>
            <button
              onClick={() => fetchData(true)}
              style={{ background: 'transparent', color: 'white', border: '1px solid white', borderRadius: '16px', padding: '6px 18px', cursor: 'pointer' }}
            >
              Retry
            </button>
          </div>
        ) :  Object.keys(groupedData).length === 0 ? (
          <div className="text-center">
            <p style={{ color: 'white', marginTop: '20px', fontSize: 'clamp(10px, calc(8px + (4) * ((100vw - 300px) / (1200 - 320))), 18px)' }}>No games scheduled for {selectedDate ? selectedDate.toLocaleDateString() : 'today'}</p>
            <DayNav
              onPrev={() => handleDateChange('prev')}
              onRefresh={() => fetchData(true)}
              onToday={handleToday}
              onNext={() => handleDateChange('next')}
            />
          </div>
        ) : (
          <>
            {isStaff && (
              <Q2Toolbar
                counts={q2Counts}
                selectedCount={selectedIds.length}
                selectionMode={selectionMode}
                isPastDay={isPastDay}
                policy={q2Policy}
                busy={q2Busy}
                message={q2Message}
                onToggleSelectionMode={() => {
                  setSelectionMode((v) => !v);
                  setSelectedIds([]);
                }}
                onClearSelection={() => setSelectedIds([])}
                onRequestSelected={() =>
                  runRequest(() => requestFixtures(selectedIds), selectedIds)
                }
                onRequestDay={() => {
                  // The set is resolved server-side against the same league-day
                  // window the slate uses, so the optimistic marks here are the
                  // page's best guess and the poll corrects them either way.
                  const unprocessed = allFixtures
                    .filter((f) => !q2StateOf(f, q2States[f.id]))
                    .map((f) => f.id);
                  runRequest(() => requestDay(league, dateStr), unprocessed);
                }}
              />
            )}
            {/* One malformed play costs the box or its card, never the slate (B29 P2.6). */}
            <ErrorBoundary name="suggestions box" resetKey={allFixtures}>
              <SuggestionsBox 
                fixtures={allFixtures} 
                userTier={userTier}
              />
            </ErrorBoundary>
            {Object.entries(groupedData).map(([gameTime, fixtures]) => (
              <React.Fragment key={gameTime}>
                <h3 style={{ color: 'white', marginTop: '20px' }}>{gameTime}</h3>
                <hr style={{ borderTop: '2px solid white', margin: '10px 0' }} />
                {fixtures.map((fixture) => (
                  <ErrorBoundary key={fixture.id} name="slate card" resetKey={fixture}>
                  <Game
                    fixture={fixture}
                    isAuthenticated={isAuthenticated}
                    showQ2={isStaff}
                    q2State={q2StateOf(fixture, q2States[fixture.id])}
                    q2Requested={Boolean(q2Requested[fixture.id])}
                    selectable={selectionMode}
                    selected={selectedIds.includes(fixture.id)}
                    onToggleSelect={toggleSelect}
                    q2Policy={q2Policy}
                    slateDate={dateStr}
                    showPicks={Boolean(userTier && userTier !== 'Anon')}
                  />
                  </ErrorBoundary>
                ))}
              </React.Fragment>
            ))}
            <DayNav
              onPrev={() => handleDateChange('prev')}
              onRefresh={() => fetchData(true)}
              onToday={handleToday}
              onNext={() => handleDateChange('next')}
            />
          </>
        )}
      </Col>
    </Container>
  );
};

export default ListOfGames;